import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Row, Col, Form, Button, Container, Card } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { MdArrowBack, MdSave, MdInfoOutline } from "react-icons/md";
import Tooltip from "@mui/material/Tooltip";

const AddInventoryItem = () => {
  const [editId, setEditId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const [categories, setCategories] = useState([]);
  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  const [form, setForm] = useState({
    item_name: "",
    category_id: "",
    unit: "",
    cost_price: 0,
    selling_price: 0,
    reorder_level: 0,
    description: "",
    is_active: "A",
  });

  // Store available stock for display only (not editable — managed via Purchase Entry)
  const [availableStock, setAvailableStock] = useState(null);

  useEffect(() => {
    // Fetch categories for dropdown
    const fetchCategories = async () => {
      try {
        const response = await axios.post(baseUrl + "/inventorycategory/", { 
          action: "READ",
          school_id: userObj.school_id 
        });
        setCategories(response.data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();

    if (location.state?.itemData) {
      const itemData = location.state.itemData;
      setForm({
        item_name: itemData.item_name || "",
        category_id: itemData.category_id || "",
        unit: itemData.unit || "",
        cost_price: itemData.cost_price || 0,
        selling_price: itemData.selling_price || 0,
        reorder_level: itemData.reorder_level || 0,
        description: itemData.description || "",
        is_active: itemData.is_active || "A",
      });
      setEditId(itemData.item_id);
      // Show current stock as read-only info when editing
      setAvailableStock(itemData.quantity ?? 0);
      window.history.replaceState({}, document.title);
    }
  }, [location, baseUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = {
      ...form,
      category_id: form.category_id || 0,
      cost_price: form.cost_price || 0,
      selling_price: form.selling_price || 0,
      reorder_level: form.reorder_level || 0,
      school_id: userObj.school_id,
      createdby: userObj.user_id || userObj.userid,
      lastmodifiedby: userObj.user_id || userObj.userid,
      is_active: editId !== null ? form.is_active : "A",
      action: editId !== null ? "UPDATE" : "CREATE"
    };

    if (editId !== null) {
      formData.item_id = editId;
    }

    try {
      await axios.post(baseUrl + "/inventoryitem/", formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      setForm({
        item_name: "",
        category_id: "",
        unit: "",
        cost_price: 0,
        selling_price: 0,
        reorder_level: 0,
        description: "",
        is_active: "A",
      });
      if (editId !== null) {
        toast.success("Record Updated Successfully");
        setTimeout(() => navigate("/inventorylist"), 1500);
      } else {
        toast.success("Record Added Successfully");
        setTimeout(() => navigate("/inventorylist"), 1500);
      }
    } catch (error) {
      console.error("Error submitting data:", error.response?.data || error.message);
      toast.error("Unexpected error occurred");
    }
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <div className="">
          <Card>
            <Card.Body>
              <form className="" onSubmit={handleSubmit}>
                <div className="">
                  <Row>
                    <Col xs={12}>
                      <h6 className="commonSectionTitle">Item Details</h6>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Item Name <span className="requiredStar">*</span></Form.Label>
                          <Form.Control
                            required
                            type="text"
                            name="item_name"
                            value={form.item_name}
                            placeholder="Enter Item Name"
                            maxLength={50}
                            onChange={handleInputChange}
                          />
                          <Form.Control.Feedback>Required</Form.Control.Feedback>
                        </Form.Group>
                      </div>
                    </Col>
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Category <span className="requiredStar">*</span></Form.Label>
                          <Form.Select
                            name="category_id"
                            value={form.category_id}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select Category</option>
                            {categories.filter(c => c.is_active === 'A').map((cat) => (
                              <option key={cat.category_id} value={cat.category_id}>
                                {cat.category_name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>
                    </Col>
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Unit (e.g. Pcs, Kg)</Form.Label>
                          <Form.Control
                            type="text"
                            name="unit"
                            value={form.unit}
                            placeholder="Unit"
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </div>
                    </Col>
                    {/* Show current stock as read-only when editing */}
                    {editId !== null && availableStock !== null && (
                      <Col xs={12} md={6} lg={4} xxl={3}>
                        <div className="commonInput">
                          <Form.Group>
                            <Form.Label>
                              Current Stock{" "}
                              <Tooltip title="Stock is managed through Purchase Entry. You cannot edit stock directly here." arrow>
                                <span style={{ cursor: "help" }}><MdInfoOutline size={16} color="#6c757d" /></span>
                              </Tooltip>
                            </Form.Label>
                            <Form.Control
                              type="number"
                              value={availableStock}
                              readOnly
                              disabled
                              style={{ backgroundColor: "#f0f0f0" }}
                            />
                          </Form.Group>
                        </div>
                      </Col>
                    )}
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Cost Price</Form.Label>
                          <Form.Control
                            type="number"
                            name="cost_price"
                            value={form.cost_price}
                            placeholder="0"
                            min="0"
                            step="0.01"
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </div>
                    </Col>
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Selling Price</Form.Label>
                          <Form.Control
                            type="number"
                            name="selling_price"
                            value={form.selling_price}
                            placeholder="0"
                            min="0"
                            step="0.01"
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </div>
                    </Col>
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>
                            Reorder Level{" "}
                            <Tooltip title="When available stock drops to or below this number, the item will show as 'LOW STOCK' in the inventory list. Set this to the minimum quantity you want to maintain before reordering." arrow>
                              <span style={{ cursor: "help" }}><MdInfoOutline size={16} color="#6c757d" /></span>
                            </Tooltip>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="reorder_level"
                            value={form.reorder_level}
                            placeholder="0"
                            min="0"
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </div>
                    </Col>
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={1}
                            name="description"
                            value={form.description}
                            placeholder="Enter Description"
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </div>
                    </Col>
                  </Row>
                </div>
                <div className="d-flex justify-content-between mt-3">
                  <div>
                    <Button
                      type="button"
                      variant="primary"
                      className="btn-info clearBtn"
                      onClick={() => setForm({
                        item_name: "",
                        category_id: "",
                        unit: "",
                        cost_price: 0,
                        selling_price: 0,
                        reorder_level: 0,
                        description: "",
                        is_active: "A",
                      })}
                    >
                      Reset
                    </Button>
                  </div>
                  <div>
                    <Button
                      type="button"
                      variant="primary"
                      className="btn-danger secondaryBtn me-2"
                      onClick={() => navigate("/inventorylist")}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      className="btn-success primaryBtn"
                    >
                      Submit
                    </Button>
                  </div>
                </div>
              </form>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default AddInventoryItem;
